import React from 'react'
import {FieldErrors, FieldValues, Path, RegisterOptions, UseFormRegister} from 'react-hook-form'
import {getFieldError} from './getFieldError'

type TextInputProps<T extends FieldValues> = {
  name: Path<T>
  label?: string
  placeholder?: string
  helperText?: string
  register: UseFormRegister<T>
  errors?: FieldErrors<T>
  required?: boolean
  type?: string
  className?: string
  inputClassName?: string
  prefix?: string
  suffix?: string
  layout?: 'stacked' | 'half'
  numeric?: boolean
  allowDecimal?: boolean
  maxLength?: number
  uppercase?: boolean
  options?: RegisterOptions<T, Path<T>>
}

export const TextInput = <T extends FieldValues>({
  name,
  label,
  placeholder,
  register,
  errors,
  required = false,
  type = 'text',
  className = '',
  inputClassName = '',
  prefix,
  suffix,
  helperText = '',
  layout = 'stacked',
  numeric = false,
  allowDecimal = true,
  maxLength,
  uppercase = false,
  options = {},
}: TextInputProps<T>) => {
  // format numeric input for saving
  const sanitizeValue = (value: string) => {
    const stringValue = value != null ? String(value) : ''
    const normalizedValue = uppercase ? stringValue.toUpperCase() : stringValue

    if (!numeric) return normalizedValue

    const numericValue = normalizedValue.replace(/,/g, '')
    return allowDecimal ? numericValue : numericValue.replace(/\D/g, '')
  }

  // onInput restrict characters
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!numeric) return
    const allowed = allowDecimal ? /[0-9.]/ : /[0-9]/
    if (!allowed.test(event.key)) {
      event.preventDefault()
    }
  }

  const error = getFieldError(errors, name)
  const wrapperClassName = layout === 'half' ? 'col-12 col-md-6 mb-0' : 'mb-5'

  return (
    <div className={`${wrapperClassName} ${className}`.trim()}>
      {label && (
        <label className='form-label fw-semibold' htmlFor={String(name)}>
          {label} {required && <span className='text-danger'>*</span>}
        </label>
      )}

      <div style={prefix || suffix ? {position: 'relative'} : undefined}>
        {prefix && (
          <span
            aria-hidden='true'
            style={{
              position: 'absolute',
              left: '0.9rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#8a94a6',
              fontWeight: 600,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {prefix}
          </span>
        )}
        {suffix && (
          <span
            aria-hidden='true'
            style={{
              position: 'absolute',
              right: '0.9rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#8a94a6',
              fontWeight: 600,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {suffix}
          </span>
        )}
        <input
          id={String(name)}
          type={type}
          placeholder={placeholder}
          maxLength={maxLength}
          inputMode={numeric ? (allowDecimal ? 'decimal' : 'numeric') : undefined}
          pattern={numeric && !allowDecimal ? '[0-9]*' : undefined}
          className={`form-control ${prefix ? 'ps-9' : ''} ${inputClassName} ${
            error ? 'is-invalid' : ''
          }`}
          style={suffix ? {paddingRight: '6.5rem'} : undefined}
          {...register(name, {
            ...options,
            required,
            setValueAs: sanitizeValue, // now properly typed
          })}
          onInput={(event) => {
            if (uppercase) {
              event.currentTarget.value = event.currentTarget.value.toUpperCase()
            }
            if (numeric && !allowDecimal) {
              event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '')
            }
          }}
          onKeyPress={handleKeyPress}
        />
      </div>
      {helperText && <small className='text-muted d-block mt-1 fs-6'>{helperText}</small>}

      {error && <div className='text-danger mt-1'>{error.message}</div>}
    </div>
  )
}
